/**
 * @fileoverview DraggableModal Context - Provides modal control methods to child components
 * Eliminates prop drilling by providing modal actions through React Context.
 * 
 * @author SmarterServices Platform Team
 * @version 1.0.0
 * @since 2025-10-21
 */

import React, { createContext, useContext, ReactNode } from 'react';

/**
 * Modal context interface defining available actions
 */
export interface DraggableModalContextType {
  /** Close the modal */
  close: () => void;
  /** Minimize the modal */
  minimize: () => void;
  /** Maximize the modal (restore from minimized) */
  maximize: () => void;
  /** Toggle minimize/maximize state */
  toggleMinimize: () => void;
  /** Bring modal to front */
  bringToFront: () => void;
  /** Check if modal is minimized */
  isMinimized: () => boolean;
  /** Get current modal position */
  getPosition: () => { x: number; y: number };
  /** Get current modal size */
  getSize: () => { width: number; height: number };
  /** Update modal position */
  updatePosition: (position: { x: number; y: number }) => void;
  /** Update modal size */
  updateSize: (size: { width: number; height: number }) => void;
  /** Update modal title */
  setTitle: (title: string) => void;
}

/**
 * Context for DraggableModal actions
 */
const DraggableModalContext = createContext<DraggableModalContextType | null>(null);

/**
 * Props for DraggableModalProvider
 */
export interface DraggableModalProviderProps {
  children: ReactNode;
  value: DraggableModalContextType;
}

/**
 * Provider component that wraps modal content with context
 * 
 * @param props - Provider props
 * @returns JSX element wrapping children with modal context
 */
export function DraggableModalProvider({ children, value }: DraggableModalProviderProps) {
  return React.createElement(DraggableModalContext.Provider, { value }, children);
}

/**
 * Hook to access DraggableModal context
 * 
 * Provides access to modal control methods from any child component
 * without prop drilling.
 * 
 * @example
 * ```tsx
 * function MyModalContent() {
 *   const modal = useDraggableModalContext();
 *   
 *   return (
 *     <Button onClick={modal.close}>
 *       Close Modal
 *     </Button>
 *   );
 * }
 * ```
 * 
 * @throws Error if used outside of DraggableModalProvider
 * @returns Modal context with control methods
 */
export function useDraggableModalContext(): DraggableModalContextType {
  const context = useContext(DraggableModalContext);
  
  if (!context) {
    throw new Error(
      'useDraggableModalContext must be used within a DraggableModalProvider. ' +
      'Make sure your component is wrapped with DraggableModal or DraggableModalProvider.'
    );
  }
  
  return context;
}

export default DraggableModalContext;
